import { useCallback, useEffect, useState } from 'react'
import type { JSX } from 'react'

import { payerShareOf, type CoverageRule } from '@lensmira/domain'
import type { MessageKey } from '@lensmira/i18n'

import { Ltr, useTranslation } from '../i18n.js'
import { MoneyInputError, formatWireForDisplay, parseMajorInput } from '../money.js'
import { statusLine } from '../queue/status-line.js'
import { useWriteSettled } from '../queue/use-settled-write.js'
import { useWriteQueue } from '../queue/use-write-queue.js'
import { DataSurface, type SurfaceState } from './DataSurface.js'

/**
 * Setting a panel up: the payer, its plans, what each plan pays and who is on it. §6.13 `CLM-001`
 * to `CLM-003`; §15.20 step 1. `FEATURES.md` puts panel and corporate accounts in v1.
 *
 * **Where this belongs: a panel on the `setup` destination, mounted by `App.tsx`.** All twenty-six
 * rail glyphs are taken and `rail.test.ts` enforces it, so a new screen is a panel on an existing
 * destination (`decisions/0026`, `decisions/0040`). `setup` is the right one rather than the only
 * one left: its rail entry is gated on `staff_create_assign_role`, which Appendix B gives owner and
 * manager, and `panel_account_manage` is held by **the same two**. So nobody who holds this row is
 * shut out of the destination that carries it, and nobody who reaches the destination is offered a
 * panel the server would refuse. `reports` would have been wrong for both reasons: it is gated on
 * `report_operational`, which every role holds.
 *
 * It is the configuration half of the panel work. `PanelStatements` on the `reports` destination is
 * the other half, and `../later_todo.md` 99 is why this one exists: "nothing in `apps/api` can
 * create a panel, so the chooser has no options and no sequence of actions in the product changes
 * that." The statement screen has been reachable and permanently empty.
 *
 * ---
 *
 * **The permission gate is inside the read effect, not only before the render.** A screen that
 * declares `useEffect` above `if (!canManage) return null` still **runs** that effect: hooks are
 * registered in order and the early return happens afterwards, so an ungated mount fires the read
 * and takes a 403 on a request nobody made. `PanelStatements` has that shape and relies on
 * `App.tsx` guarding the mount site, which works and is one gate rather than two. Here the effect
 * checks the row itself, so the screen is safe mounted either way, and `panel-accounts.test.tsx`
 * asserts the loader is never called rather than that nothing renders.
 *
 * The prop is still load-bearing with a guard at the mount site too: the rail is built from
 * `staff.permissions` as the context read returns them rather than from the compiled matrix, so a
 * tenant whose rows differ from Appendix B's defaults can hand this app somebody who reaches Setup
 * without this row.
 *
 * **Every refusal this screen can provoke is pre-checked, because a refused write stops the till.**
 * A refused write sits `blocked` at the head of the write queue and holds every write behind it
 * (`OFF-001a`, `OFF-005`), so a sale queued after a mistyped percentage would not go out either.
 * The coverage rule is checked by calling `payerShareOf`, the domain function the service itself
 * calls, so the counter and the server agree about what a usable rule is by construction rather
 * than by two lists that can drift. The checks this screen makes itself are the shape ones the
 * service states: a name, a three-letter currency, a chosen kind, a chosen policy, an explicit
 * authorisation answer (the spec defaults it to nothing and so does the service), and a check date
 * required exactly when a member is not `unverified`.
 *
 * **One date field, shown only when a status needs one**, which makes
 * `ELIGIBILITY_DATE_UNEXPECTED` unreachable from here rather than pre-checked: there is no field to
 * fill in while every status on screen is `unverified`. The add form and the re-check share it,
 * because they are two statuses of one person in one sitting and two fields labelled "Checked on"
 * would be ambiguous to read out.
 *
 * **Members are not listed, and the screen says so.** No read anywhere returns the members of a
 * plan: `PanelView` carries plans and coverage only, and `MemberView` comes back from the two
 * member writes and nowhere else. So a re-check is offered for the member added in this sitting,
 * whose id and version came back on the write, and never for one typed in: a guessed version is a
 * 409, and a 409 is a refusal. The read that would fix it is noted with the slice.
 *
 * **A rate that is not this caller's to see is shown as withheld, never as zero.** A panel's
 * negotiated rate is treated as cost price (`ROLE-001a`'s per-branch grant), so `GET /panels/:id`
 * omits the rule rather than zeroing it. Rendering an absent rule as "pays nothing" would be a
 * different and false statement about somebody's commercial terms.
 */

// ---------------------------------------------------------------------------------------------
// What this panel needs. Shaped to `panels.service.ts` and declared here rather than imported:
// `apps/*` never import from each other, and these are the counter's view types.
// ---------------------------------------------------------------------------------------------

/** A row of `GET /panels`. The same four fields `PanelStatements` reads, so one loader serves both. */
export interface PanelRow {
  readonly id: string
  readonly name: string
  readonly currency: string
  readonly deactivated: boolean
}

/** One money figure on the wire: minor units and a currency, together, never a float. */
export interface PanelMoney {
  readonly minor: string
  readonly currency: string
}

/** A coverage rule as `GET /panels/:panelId` sends it. `rule` is absent for a caller without the grant. */
export interface CoverageRowData {
  readonly category: string
  readonly rule?: {
    readonly kind: string
    readonly amount?: PanelMoney
    readonly basisPoints?: number
    readonly payerShareRounding?: string
  }
}

export interface PanelPlanData {
  readonly id: string
  readonly name: string
  readonly eligibilityPolicy: string
  readonly authorisationRequired: boolean
  readonly deactivated: boolean
  readonly coverage: readonly CoverageRowData[]
}

export interface PanelDetailData {
  readonly id: string
  readonly name: string
  readonly kind: string
  readonly currency: string
  readonly plans: readonly PanelPlanData[]
}

/** A customer the search found. Only what the chooser shows and what the write sends. */
export interface PanelCustomer {
  readonly id: string
  readonly name: string
}

export interface PanelAccountsProps {
  readonly loadPanels: () => Promise<readonly PanelRow[]>
  readonly loadPanel: (panelId: string) => Promise<PanelDetailData>
  readonly searchCustomers: (query: string) => Promise<readonly PanelCustomer[]>
  /** Appendix B's `Panel account · manage`. Without it there is no panel and no read. */
  readonly canManage: boolean
  /** The branch's own today, so a check date defaults to the day the shop is having. */
  readonly today: string
}

/** `panel_kind`, `eligibility_policy` and `eligibility_status` in `packages/db`'s claims schema. */
const KINDS = ['insurer', 'corporate_panel'] as const
const POLICIES = ['require_verified', 'allow_unverified'] as const
const STATUSES = ['unverified', 'verified', 'ineligible'] as const
const RULE_KINDS = ['excluded', 'fixed_allowance', 'copay', 'schedule_price', 'percentage'] as const

const KIND_WORD: Readonly<Record<string, MessageKey>> = {
  insurer: 'panelAccounts.kindInsurer',
  corporate_panel: 'panelAccounts.kindCorporate',
}
const POLICY_WORD: Readonly<Record<string, MessageKey>> = {
  require_verified: 'panelAccounts.policyVerified',
  allow_unverified: 'panelAccounts.policyUnverified',
}
const STATUS_WORD: Readonly<Record<string, MessageKey>> = {
  unverified: 'panelAccounts.statusUnverified',
  verified: 'panelAccounts.statusVerified',
  ineligible: 'panelAccounts.statusIneligible',
}
const RULE_WORD: Readonly<Record<string, MessageKey>> = {
  excluded: 'panelAccounts.ruleExcluded',
  fixed_allowance: 'panelAccounts.ruleAllowance',
  copay: 'panelAccounts.ruleCopay',
  schedule_price: 'panelAccounts.ruleSchedule',
  percentage: 'panelAccounts.rulePercent',
}

/** The amount this rule kind carries, if it carries one. `excluded` and `percentage` do not. */
const CARRIES_AMOUNT = new Set(['fixed_allowance', 'copay', 'schedule_price'])

/** Whether a name is already taken, by the same `lower(name)` rule the unique indexes use. */
const sameName = (rows: readonly { readonly name: string }[] | null, typed: string): boolean => {
  const wanted = typed.trim().toLowerCase()
  return wanted !== '' && (rows ?? []).some((row) => row.name.trim().toLowerCase() === wanted)
}

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/
const SEARCH_FLOOR = 2

/** One coverage line being built, before it becomes a rule. */
interface DraftCoverage {
  readonly category: string
  readonly kind: string
  /** Minor units, already parsed from what was typed, so the plan write sends no arithmetic. */
  readonly amountMinor: string
  readonly basisPoints: number
  readonly rounding: 'down' | 'up'
}

/** What `POST /panel-plans/:planId/members` answers, read defensively: it arrives as `unknown`. */
interface AddedMember {
  readonly id: string
  readonly planId: string
  readonly eligibilityStatus: string
  readonly version: number
}

const memberOf = (response: unknown): AddedMember | null => {
  const body = typeof response === 'object' && response !== null ? response : {}
  const member = (body as { member?: unknown }).member
  if (typeof member !== 'object' || member === null) return null
  const row = member as Record<string, unknown>
  if (typeof row['id'] !== 'string' || typeof row['version'] !== 'number') return null
  return {
    id: row['id'],
    planId: String(row['planId'] ?? ''),
    eligibilityStatus: String(row['eligibilityStatus'] ?? 'unverified'),
    version: row['version'],
  }
}

/**
 * Whole basis points from what somebody typed as a percentage, by string arithmetic.
 *
 * `claim-support.ts`'s `basisPointsOf` does the same conversion in the other direction for the same
 * reason: 12.5% is 1250 basis points exactly, and going through a float can land one point off what
 * `claim_line_split_matches_rule` computes. Returns `null` for anything that is not a percentage.
 */
function basisPointsFrom(input: string): number | null {
  const match = /^(\d{1,3})(?:\.(\d{1,2}))?$/.exec(input.trim())
  if (!match) return null
  const whole = Number(match[1])
  const frac = Number(`${match[2] ?? ''}00`.slice(0, 2))
  const points = whole * 100 + frac
  return points > 10000 ? null : points
}

/** The rule as the wire carries it, from a draft line. */
function wireRule(draft: DraftCoverage): Record<string, unknown> {
  if (draft.kind === 'percentage') {
    return {
      kind: 'percentage',
      basisPoints: draft.basisPoints,
      payerShareRounding: draft.rounding,
    }
  }
  if (CARRIES_AMOUNT.has(draft.kind)) return { kind: draft.kind, amountMinor: draft.amountMinor }
  return { kind: draft.kind }
}

/** The same rule as the domain's type, for the pre-check the service also runs. */
function domainRule(draft: DraftCoverage): CoverageRule {
  const minor = BigInt(draft.amountMinor === '' ? '0' : draft.amountMinor)
  switch (draft.kind) {
    case 'fixed_allowance':
      return { kind: 'fixed_allowance', allowanceMinor: minor }
    case 'copay':
      return { kind: 'copay', copayMinor: minor }
    case 'schedule_price':
      return { kind: 'schedule_price', scheduleMinor: minor }
    case 'percentage':
      return {
        kind: 'percentage',
        basisPoints: draft.basisPoints,
        payerShareRounding: draft.rounding,
      }
    default:
      return { kind: 'excluded' }
  }
}

export function PanelAccounts({
  loadPanels,
  loadPanel,
  searchCustomers,
  canManage,
  today,
}: PanelAccountsProps): JSX.Element | null {
  const { t } = useTranslation()
  const { enqueue, snapshot } = useWriteQueue()

  const [panels, setPanels] = useState<readonly PanelRow[] | null>(null)
  const [panelsFailed, setPanelsFailed] = useState(false)
  const [openId, setOpenId] = useState('')
  const [detail, setDetail] = useState<PanelDetailData | null>(null)
  const [detailFailed, setDetailFailed] = useState(false)

  const [panelName, setPanelName] = useState('')
  const [panelKind, setPanelKind] = useState('')
  const [panelCurrency, setPanelCurrency] = useState('')

  const [planName, setPlanName] = useState('')
  const [planPolicy, setPlanPolicy] = useState('')
  const [planAuth, setPlanAuth] = useState('')
  const [coverage, setCoverage] = useState<readonly DraftCoverage[]>([])
  const [category, setCategory] = useState('')
  const [ruleKind, setRuleKind] = useState('')
  const [amount, setAmount] = useState('')
  const [percent, setPercent] = useState('')
  const [rounding, setRounding] = useState<'down' | 'up'>('down')

  const [memberPlan, setMemberPlan] = useState('')
  const [query, setQuery] = useState('')
  const [matches, setMatches] = useState<readonly PanelCustomer[] | null>(null)
  const [chosen, setChosen] = useState<PanelCustomer | null>(null)
  const [status, setStatus] = useState('unverified')
  const [checkedOn, setCheckedOn] = useState(today)
  const [added, setAdded] = useState<AddedMember | null>(null)
  const [recheckStatus, setRecheckStatus] = useState('verified')

  const [error, setError] = useState<string | null>(null)
  const [writeId, setWriteId] = useState<string | null>(null)

  /**
   * The list, read only by somebody who holds the row.
   *
   * The gate is inside the effect rather than only before the render, for the reason in the
   * docstring: an early return does not unregister a hook that has already run.
   */
  useEffect(() => {
    if (!canManage) return
    let live = true
    setPanelsFailed(false)
    void loadPanels().then(
      (rows) => {
        if (live) setPanels(rows)
      },
      () => {
        if (live) setPanelsFailed(true)
      },
    )
    return () => {
      live = false
    }
  }, [canManage, loadPanels])

  const openPanel = useCallback(
    (panelId: string) => {
      if (!canManage || panelId === '') return
      setDetail(null)
      setDetailFailed(false)
      void loadPanel(panelId).then(
        (row) => setDetail(row),
        () => setDetailFailed(true),
      )
    },
    [canManage, loadPanel],
  )

  useEffect(() => {
    openPanel(openId)
  }, [openId, openPanel])

  useEffect(() => {
    if (!canManage || query.trim().length < SEARCH_FLOOR) {
      setMatches(null)
      return
    }
    let live = true
    void searchCustomers(query.trim()).then(
      (rows) => {
        if (live) setMatches(rows)
      },
      () => {
        if (live) setMatches([])
      },
    )
    return () => {
      live = false
    }
  }, [canManage, query, searchCustomers])

  /**
   * Re-read once the **server** has the write, not once the queue has it.
   *
   * `enqueue` resolves as soon as the write is persisted locally, so a read fired on the next line
   * returns the state from before the POST went out (`use-settled-write.ts`).
   */
  useWriteSettled(snapshot, writeId, (write) => {
    setWriteId(null)
    const member = memberOf(write.response)
    if (member !== null) {
      setAdded(member)
      setChosen(null)
      setQuery('')
      setMatches(null)
    }
    void loadPanels().then(
      (rows) => setPanels(rows),
      () => setPanelsFailed(true),
    )
    if (openId !== '') openPanel(openId)
  })

  const busy = writeId !== null

  const send = useCallback(
    async (path: string, label: string, body: Record<string, unknown>) => {
      const write = (await enqueue({ method: 'POST', path, label, body })) as { id: string }
      setWriteId(write.id)
    },
    [enqueue],
  )

  const addPanel = useCallback(async () => {
    if (panelName.trim() === '' || panelKind === '') {
      setError(t('panelAccounts.needed'))
      return
    }
    if (!/^[A-Z]{3}$/.test(panelCurrency.trim())) {
      // `FIELD_INVALID` on the server, a 422, and a 422 blocks the queue.
      setError(t('panelAccounts.badCurrency'))
      return
    }
    // `panel_name_key` is unique on `lower(name)` across the tenant, so a second Acme is a
    // duplicate-key refusal: "That record already exists: panel_name_key." Checked against the
    // list this screen already holds rather than left to the server, because that refusal is a
    // 409 at the head of the write queue and it stops every sale behind it.
    if (sameName(panels, panelName)) {
      setError(t('panelAccounts.duplicate'))
      return
    }
    setError(null)
    await send('/panels', t('panelAccounts.queueLabel', { name: panelName.trim() }), {
      name: panelName.trim(),
      kind: panelKind,
      currency: panelCurrency.trim(),
    })
    setPanelName('')
    setPanelKind('')
    setPanelCurrency('')
  }, [panelCurrency, panelKind, panelName, panels, send, t])

  const addCoverage = useCallback(() => {
    if (category.trim() === '' || ruleKind === '') {
      setError(t('panelAccounts.needed'))
      return
    }
    const currency = detail?.currency ?? 'PKR'
    let amountMinor = '0'
    if (CARRIES_AMOUNT.has(ruleKind)) {
      try {
        amountMinor = parseMajorInput(amount, currency).toString()
      } catch (failure) {
        setError(
          failure instanceof MoneyInputError ? t('panelAccounts.badAmount') : String(failure),
        )
        return
      }
    }
    let points = 0
    if (ruleKind === 'percentage') {
      const parsed = basisPointsFrom(percent)
      if (parsed === null) {
        setError(t('panelAccounts.badAmount'))
        return
      }
      points = parsed
    }
    const draft: DraftCoverage = {
      category: category.trim(),
      kind: ruleKind,
      amountMinor,
      basisPoints: points,
      rounding,
    }
    try {
      // The domain function the service calls on every rule before it stores one. A rule this
      // refuses is `RULE_INVALID` or `RULE_UNKNOWN` on the wire, and the sentence is the domain's.
      payerShareOf(domainRule(draft), 0n)
    } catch (failure) {
      setError((failure as Error).message)
      return
    }
    setError(null)
    // `panel_plan_coverage_category_key` is unique on (plan_id, `lower(category)`), so "Frame"
    // replaces "frame" here as it would in the table. A case-sensitive filter sends a plan with
    // two rows the index refuses, and the whole plan is then one duplicate-key refusal.
    setCoverage((rows) => [
      ...rows.filter((row) => row.category.toLowerCase() !== draft.category.toLowerCase()),
      draft,
    ])
    setCategory('')
    setRuleKind('')
    setAmount('')
    setPercent('')
  }, [amount, category, detail, percent, rounding, ruleKind, t])

  const addPlan = useCallback(async () => {
    if (detail === null) return
    // `panel_plan_name_key` is unique on (panel_id, `lower(name)`): the same refusal, per panel.
    if (sameName(detail.plans, planName)) {
      setError(t('panelAccounts.duplicate'))
      return
    }
    if (planName.trim() === '' || planPolicy === '' || planAuth === '') {
      // `authorisationRequired` has no default in the spec and none in the service: it refuses
      // `FIELD_REQUIRED` rather than guessing, so this asks rather than ticking a box.
      setError(t('panelAccounts.needed'))
      return
    }
    setError(null)
    await send(
      `/panels/${encodeURIComponent(detail.id)}/plans`,
      t('panelAccounts.queueLabel', { name: planName.trim() }),
      {
        name: planName.trim(),
        eligibilityPolicy: planPolicy,
        authorisationRequired: planAuth === 'yes',
        coverage: coverage.map((row) => ({ category: row.category, rule: wireRule(row) })),
      },
    )
    setPlanName('')
    setPlanPolicy('')
    setPlanAuth('')
    setCoverage([])
  }, [coverage, detail, planAuth, planName, planPolicy, send, t])

  const addMember = useCallback(async () => {
    if (memberPlan === '' || chosen === null) {
      setError(t('panelAccounts.needed'))
      return
    }
    if (status !== 'unverified' && !ISO_DAY.test(checkedOn)) {
      setError(t('panelAccounts.badDate'))
      return
    }
    setError(null)
    await send(
      `/panel-plans/${encodeURIComponent(memberPlan)}/members`,
      t('panelAccounts.queueLabel', { name: chosen.name }),
      {
        customerId: chosen.id,
        eligibilityStatus: status,
        ...(status === 'unverified' ? {} : { eligibilityCheckedOn: checkedOn }),
      },
    )
  }, [checkedOn, chosen, memberPlan, send, status, t])

  const recheck = useCallback(async () => {
    if (added === null) return
    if (recheckStatus !== 'unverified' && !ISO_DAY.test(checkedOn)) {
      setError(t('panelAccounts.badDate'))
      return
    }
    setError(null)
    await send(
      `/panel-members/${encodeURIComponent(added.id)}/eligibility`,
      t('panelAccounts.queueLabel', { name: detail?.name ?? '' }),
      {
        // From the write that created the row, never from anything typed: a guessed version is a
        // 409, and a 409 sits blocked at the head of the queue.
        expectedVersion: added.version,
        status: recheckStatus,
        ...(recheckStatus === 'unverified' ? {} : { checkedOn }),
      },
    )
  }, [added, checkedOn, detail, recheckStatus, send, t])

  // Appendix B's `Panel account · manage`. A role without it gets no panel, not a disabled one.
  if (!canManage) return null

  const line = statusLine(t, snapshot, writeId, t('queue.subject.panelAccount'))
  const listState: SurfaceState = panelsFailed
    ? { kind: 'error', message: t('panelAccounts.readFailed') }
    : panels === null
      ? { kind: 'loading' }
      : panels.length === 0
        ? { kind: 'empty' }
        : { kind: 'ready' }

  const ruleText = (row: CoverageRowData): string => {
    if (row.rule === undefined) return t('panelAccounts.ruleWithheld')
    const word = t(RULE_WORD[row.rule.kind] ?? 'panelAccounts.ruleExcluded')
    if (row.rule.amount !== undefined) {
      return `${word} ${formatWireForDisplay(row.rule.amount.minor, row.rule.amount.currency)}`
    }
    if (row.rule.basisPoints !== undefined) {
      return `${word} ${String(row.rule.basisPoints / 100)}`
    }
    return word
  }

  return (
    <section className="lm-panel-accounts" aria-labelledby="panel-accounts-heading">
      <h2 id="panel-accounts-heading" className="lm-panel-accounts__heading">
        {t('panelAccounts.heading')}
      </h2>
      {error === null ? null : (
        <p className="lm-error" role="alert">
          {error}
        </p>
      )}
      {line === null ? null : (
        <p className="lm-queue-state" role="status">
          {line}
        </p>
      )}

      <DataSurface
        state={listState}
        shape="rows"
        rows={3}
        what={t('panelAccounts.heading')}
        emptyMessage={t('panelAccounts.noneYet')}
      >
        <ul className="lm-panel-accounts__list">
          {(panels ?? []).map((row) => (
            <li key={row.id}>
              <button
                type="button"
                className="lm-button lm-button--secondary"
                aria-pressed={openId === row.id}
                onClick={() => setOpenId(row.id)}
              >
                {t('panelAccounts.panelLine', { name: row.name, currency: row.currency })}
              </button>
            </li>
          ))}
        </ul>
      </DataSurface>

      {/* `CLM-001`'s payer. */}
      <h3 className="lm-panel-accounts__sub">{t('panelAccounts.addPanel')}</h3>
      <label className="lm-field">
        <span className="lm-field__label">{t('panelAccounts.nameLabel')}</span>
        <input
          className="lm-input"
          dir="auto"
          autoComplete="off"
          value={panelName}
          onChange={(e) => setPanelName(e.currentTarget.value)}
        />
      </label>
      <label className="lm-field">
        <span className="lm-field__label">{t('panelAccounts.kindLabel')}</span>
        <select
          className="lm-input"
          value={panelKind}
          onChange={(e) => setPanelKind(e.currentTarget.value)}
        >
          <option value="" />
          {KINDS.map((kind) => (
            <option key={kind} value={kind}>
              {t(KIND_WORD[kind] ?? 'panelAccounts.kindInsurer')}
            </option>
          ))}
        </select>
      </label>
      <label className="lm-field">
        <span className="lm-field__label">{t('panelAccounts.currencyLabel')}</span>
        {/* Western digits and Latin letters in every language, so the field never mirrors. */}
        <input
          className="lm-input"
          dir="ltr"
          autoComplete="off"
          maxLength={3}
          value={panelCurrency}
          onChange={(e) => setPanelCurrency(e.currentTarget.value.toUpperCase())}
        />
      </label>
      <button
        type="button"
        className="lm-button lm-button--primary"
        disabled={busy}
        onClick={() => {
          void addPanel()
        }}
      >
        {t('panelAccounts.addPanel')}
      </button>

      {detailFailed ? <p className="lm-hint">{t('panelAccounts.readFailed')}</p> : null}

      {detail === null ? null : (
        <>
          <h3 className="lm-panel-accounts__sub">
            {t('panelAccounts.plansHeading', { name: detail.name })}
          </h3>
          {detail.plans.length === 0 ? (
            <p className="lm-empty">{t('panelAccounts.noPlans')}</p>
          ) : null}
          <ul className="lm-panel-accounts__plans">
            {detail.plans.map((plan) => (
              <li key={plan.id} className="lm-panel-plan">
                <span className="lm-panel-plan__name">
                  {t('panelAccounts.planLine', {
                    name: plan.name,
                    policy: t(
                      POLICY_WORD[plan.eligibilityPolicy] ?? 'panelAccounts.policyVerified',
                    ),
                  })}
                </span>
                <ul className="lm-panel-plan__coverage">
                  {plan.coverage.map((row) => (
                    <li key={row.category}>
                      {t('panelAccounts.coverageLine', {
                        category: row.category,
                        rule: ruleText(row),
                      })}
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>

          {/* `CLM-002` and `CLM-003`: the plan and what it pays, in one write. */}
          <h3 className="lm-panel-accounts__sub">{t('panelAccounts.addPlan')}</h3>
          <label className="lm-field">
            <span className="lm-field__label">{t('panelAccounts.planNameLabel')}</span>
            <input
              className="lm-input"
              dir="auto"
              autoComplete="off"
              value={planName}
              onChange={(e) => setPlanName(e.currentTarget.value)}
            />
          </label>
          <label className="lm-field">
            <span className="lm-field__label">{t('panelAccounts.policyLabel')}</span>
            <select
              className="lm-input"
              value={planPolicy}
              onChange={(e) => setPlanPolicy(e.currentTarget.value)}
            >
              <option value="" />
              {POLICIES.map((policy) => (
                <option key={policy} value={policy}>
                  {t(POLICY_WORD[policy] ?? 'panelAccounts.policyVerified')}
                </option>
              ))}
            </select>
          </label>
          <label className="lm-field">
            <span className="lm-field__label">{t('panelAccounts.authLabel')}</span>
            <select
              className="lm-input"
              value={planAuth}
              onChange={(e) => setPlanAuth(e.currentTarget.value)}
            >
              <option value="" />
              <option value="yes">{t('panelAccounts.yes')}</option>
              <option value="no">{t('panelAccounts.no')}</option>
            </select>
          </label>

          <ul className="lm-panel-accounts__draft">
            {coverage.map((row) => (
              <li key={row.category}>
                {t('panelAccounts.coverageLine', {
                  category: row.category,
                  rule: t(RULE_WORD[row.kind] ?? 'panelAccounts.ruleExcluded'),
                })}
              </li>
            ))}
          </ul>
          <label className="lm-field">
            <span className="lm-field__label">{t('panelAccounts.categoryLabel')}</span>
            <input
              className="lm-input"
              dir="auto"
              autoComplete="off"
              value={category}
              onChange={(e) => setCategory(e.currentTarget.value)}
            />
          </label>
          <label className="lm-field">
            <span className="lm-field__label">{t('panelAccounts.ruleLabel')}</span>
            <select
              className="lm-input"
              value={ruleKind}
              onChange={(e) => setRuleKind(e.currentTarget.value)}
            >
              <option value="" />
              {RULE_KINDS.map((kind) => (
                <option key={kind} value={kind}>
                  {t(RULE_WORD[kind] ?? 'panelAccounts.ruleExcluded')}
                </option>
              ))}
            </select>
          </label>
          {CARRIES_AMOUNT.has(ruleKind) ? (
            <label className="lm-field">
              <span className="lm-field__label">{t('panelAccounts.amountLabel')}</span>
              <input
                className="lm-input"
                dir="ltr"
                inputMode="decimal"
                autoComplete="off"
                value={amount}
                onChange={(e) => setAmount(e.currentTarget.value)}
              />
            </label>
          ) : null}
          {ruleKind === 'percentage' ? (
            <>
              <label className="lm-field">
                <span className="lm-field__label">{t('panelAccounts.percentLabel')}</span>
                <input
                  className="lm-input"
                  dir="ltr"
                  inputMode="decimal"
                  autoComplete="off"
                  value={percent}
                  onChange={(e) => setPercent(e.currentTarget.value)}
                />
              </label>
              <label className="lm-field">
                {/* `CLM-003` leaves the direction unspecified, so the domain requires it. */}
                <span className="lm-field__label">{t('panelAccounts.roundingLabel')}</span>
                <select
                  className="lm-input"
                  value={rounding}
                  onChange={(e) => setRounding(e.currentTarget.value === 'up' ? 'up' : 'down')}
                >
                  <option value="down">{t('panelAccounts.roundDown')}</option>
                  <option value="up">{t('panelAccounts.roundUp')}</option>
                </select>
              </label>
            </>
          ) : null}
          <button type="button" className="lm-button lm-button--secondary" onClick={addCoverage}>
            {t('panelAccounts.addRule')}
          </button>
          <button
            type="button"
            className="lm-button lm-button--primary"
            disabled={busy}
            onClick={() => {
              void addPlan()
            }}
          >
            {t('panelAccounts.addPlan')}
          </button>

          {/* §15.20 step 1: employees are ordinary customers linked to the panel. */}
          <h3 className="lm-panel-accounts__sub">{t('panelAccounts.membersHeading')}</h3>
          <p className="lm-hint">{t('panelAccounts.membersHint')}</p>
          <label className="lm-field">
            <span className="lm-field__label">{t('panelAccounts.planLabel')}</span>
            <select
              className="lm-input"
              value={memberPlan}
              onChange={(e) => setMemberPlan(e.currentTarget.value)}
            >
              <option value="" />
              {detail.plans.map((plan) => (
                <option key={plan.id} value={plan.id}>
                  {plan.name}
                </option>
              ))}
            </select>
          </label>
          <label className="lm-field">
            <span className="lm-field__label">{t('panelAccounts.customerLabel')}</span>
            <input
              className="lm-input"
              dir="auto"
              autoComplete="off"
              value={chosen === null ? query : chosen.name}
              onChange={(e) => {
                setChosen(null)
                setQuery(e.currentTarget.value)
              }}
            />
          </label>
          {chosen === null && matches !== null ? (
            <ul className="lm-panel-accounts__matches">
              {matches.map((row) => (
                <li key={row.id}>
                  <button
                    type="button"
                    className="lm-button lm-button--secondary"
                    onClick={() => setChosen(row)}
                  >
                    <Ltr>{row.name}</Ltr>
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
          <label className="lm-field">
            <span className="lm-field__label">{t('panelAccounts.statusLabel')}</span>
            <select
              className="lm-input"
              value={status}
              onChange={(e) => setStatus(e.currentTarget.value)}
            >
              {STATUSES.map((value) => (
                <option key={value} value={value}>
                  {t(STATUS_WORD[value] ?? 'panelAccounts.statusUnverified')}
                </option>
              ))}
            </select>
          </label>
          {status === 'unverified' && !(added !== null && recheckStatus !== 'unverified') ? null : (
            <label className="lm-field">
              <span className="lm-field__label">{t('panelAccounts.checkedOnLabel')}</span>
              <input
                className="lm-input"
                dir="ltr"
                type="date"
                value={checkedOn}
                onChange={(e) => setCheckedOn(e.currentTarget.value)}
              />
            </label>
          )}
          <button
            type="button"
            className="lm-button lm-button--primary"
            disabled={busy}
            onClick={() => {
              void addMember()
            }}
          >
            {t('panelAccounts.addMember')}
          </button>

          {added === null ? null : (
            <>
              <label className="lm-field">
                <span className="lm-field__label">{t('panelAccounts.recheckStatusLabel')}</span>
                <select
                  className="lm-input"
                  value={recheckStatus}
                  onChange={(e) => setRecheckStatus(e.currentTarget.value)}
                >
                  {STATUSES.map((value) => (
                    <option key={value} value={value}>
                      {t(STATUS_WORD[value] ?? 'panelAccounts.statusUnverified')}
                    </option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                className="lm-button lm-button--primary"
                disabled={busy}
                onClick={() => {
                  void recheck()
                }}
              >
                {t('panelAccounts.recheck')}
              </button>
            </>
          )}
        </>
      )}
    </section>
  )
}
